import { PersonLabelContext, UserStatus } from '@bp/bp-graphql-types';
import { GroupRoles, ProfileRoles } from '@bp/pim-auth-constants';
import {
  AddIcon,
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Grid,
  GridColumn,
  GridRow,
  SendIcon,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { CourseMembersModal } from 'components/Courses/modals/CourseMembersModal';
import { LoadingModal } from 'components/LoadingModal/LoadingModal';
import { PersonsLabelFilter } from 'components/Persons/components/PersonsLabelFilter/PersonsLabelFilter';
import { CreateUserModal } from 'components/Persons/modals/CreateUserModal';
import { PersonLabelSource, PersonLabelType, PersonListType } from 'components/Persons/types/personTypes';
import { accountHasPerson, labelIsInLabels } from 'components/Persons/utils/personUtils';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useConfirm } from 'hooks/useConfirm';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { getProfileRoleFromRoles } from 'utils/profileRoles';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { SimplePersonsList } from '../../../components/Persons/components/PersonsList/SimplePersonsList';
import { usePersonBulkActions } from '../../../components/Persons/hooks/usePersonBulkActions';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { redirectToDmMessagePage } from '../../../matrix/utils/redirectToDmMessagePage';
import styles from './CoursePersonsSubpage.module.scss';

const UpdateGroupsMutationDocument = graphql(`
  mutation BpUpdateGroupsInCourseSubpage($update: BpGroupUpdateInput!, $uuid: ID!) {
    updateBpGroup(update: $update, uuid: $uuid) {
      __typename
      uuid
      editors {
        __typename
        uuid
      }
      viewers {
        __typename
        uuid
      }
    }
  }
`);

const CoursePersonsSubpagePageQueryDocument = graphql(`
  query CoursePersonsSubpagePageData(
    $courseUuid: ID!
    $labelUuids: [ID!]!
    $schoolyearUuid: ID!
    $labelContext: PersonLabelContext!
    $organizationUuid: ID!
  ) {
    threemaLicenseCount: threemas(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      licenseCount
    }
    usedThreemaLicenses: profiles(
      where: {
        threemaConnection_SOME: { edge: { enabled: true }, node: { organization: { uuid: $organizationUuid } } }
      }
    ) {
      __typename
      uuid
    }
    courses: groups(where: { uuid: $courseUuid, type: course }) {
      __typename
      uuid
      isCommunity
      managedBy
      admins {
        __typename
        uuid
        displayName
        organization {
          __typename
          uuid
        }
      }
      editors {
        __typename
        uuid
        updatedAt
        firstName
        lastName
        displayName
        listName
        email
        birthday
        user {
          __typename
          uuid
          status
        }
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
      viewers {
        __typename
        uuid
        updatedAt
        firstName
        lastName
        displayName
        listName
        email
        birthday
        user {
          __typename
          uuid
          status
        }
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
    }
    ...PersonsLabelFilterFields
    ...PersonsListFields
  }
`);

const CollaborationOrganizationsQuery = graphql(`
  query CollaborationOrganizationsData($organizationUuid: ID!) {
    collaboratingOrganizations(uuid: $organizationUuid) {
      __typename
      uuid
      name
    }
  }
`);

type CoursePersonListType = PersonListType & {
  organizationUuid: string;
  displayName: string | null;
  groupRole: GroupRoles;
  profileRole: ProfileRoles;
};

export const CoursePersonsSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const myOrganizationUuid = pimAuthClaims.getOrganizationUuid();
  const { threemaLicense, cloudLicense, currentSchoolYear } = useContext(OrganizationConfigContext);

  const { ConfirmationDialog, confirm: confirmRemove } = useConfirm({
    defaultTitle: t('courses.removeMember'),
    defaultConfirmText: t('common.remove'),
  });

  const { courseUuid } = useParams<{ courseUuid: string }>();
  const navigate = useNavigate();

  const [, bpUpdateGroup] = useMutation(UpdateGroupsMutationDocument);

  const [activeSources, setActiveSources] = useState<PersonLabelSource[]>(['ProfileRole']);
  const [filterMode, setFilterMode] = useState<Record<PersonLabelSource, 'and' | 'or'>>({
    AccessRole: 'and',
    AddOn: 'and',
    Child: 'and',
    Class: 'and',
    ClassGroup: 'and',
    GroupRole: 'and',
    Parent: 'and',
    ProfileRole: 'and',
    UserStatus: 'and',
  });
  const [search, setSearch] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<PersonLabelType[]>([]);

  const [personData, setPersonData] = useState<{
    uuid?: string;
    status?: string;
    roles?: string[];
    addContext?: GroupRoles;
  } | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [membersModalOpen, setMembersModalOpen] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>();

  const [{ data }, refetchCourseData] = useQuery({
    query: CoursePersonsSubpagePageQueryDocument,
    variables: {
      courseUuid: courseUuid ?? '',
      organizationUuid: myOrganizationUuid,
      labelUuids: courseUuid ? [courseUuid] : [],
      schoolyearUuid: currentSchoolYear.uuid,
      labelContext: PersonLabelContext.Group,
    },
  });

  const [{ data: collaborations }] = useQuery({
    query: CollaborationOrganizationsQuery,
    variables: { organizationUuid: myOrganizationUuid },
    pause: !data?.courses[0].isCommunity,
  });

  const course = data?.courses[0];

  const organizations = [
    {
      uuid: myOrganizationUuid,
      name: t('institution.myInstitution'),
    },
    ...(collaborations?.collaboratingOrganizations.filter((o) => {
      const hasEditors = course?.editors.some((e) => e.organization.uuid === o.uuid);
      const hasViewers = course?.viewers.some((v) => v.organization.uuid === o.uuid);
      const hasAdmins = course?.admins.some((a) => a.organization.uuid === o.uuid);
      return hasEditors || hasViewers || hasAdmins;
    }) ?? []),
  ];

  const permissions = usePermissionChecker();

  const isBpCourse = course?.managedBy === 'bp';

  const admins = course?.admins ?? [];

  const teachers: CoursePersonListType[] = useMemo(
    () =>
      course?.editors
        .sort((a, b) => naturalStringSort(a.listName ?? '', b.listName ?? ''))
        .map((e) => ({
          uuid: e.uuid,
          organizationUuid: e.organization.uuid,
          firstName: e.firstName ?? null,
          lastName: e.lastName ?? null,
          displayName: e.displayName ?? null,
          listName: e.listName ?? null,
          email: e.email ?? null,
          birthday: e.birthday ?? null,
          userStatus: e.user?.status ?? UserStatus.None,
          updatedAt: e.updatedAt,
          groupRole: GroupRoles.Editor,
          profileRole: getProfileRoleFromRoles(e.organizationRoles),
        })) ?? [],
    [course?.editors],
  );

  const students: CoursePersonListType[] = useMemo(
    () =>
      course?.viewers
        .sort((a, b) => naturalStringSort(a.listName ?? '', b.listName ?? ''))
        .map((v) => ({
          uuid: v.uuid,
          organizationUuid: v.organization.uuid,
          firstName: v.firstName ?? null,
          lastName: v.lastName ?? null,
          displayName: v.displayName ?? null,
          listName: v.listName ?? null,
          email: v.email ?? null,
          birthday: v.birthday ?? null,
          userStatus: v.user?.status ?? UserStatus.None,
          updatedAt: v.updatedAt,
          groupRole: GroupRoles.Viewer,
          profileRole: getProfileRoleFromRoles(v.organizationRoles),
        })) ?? [],
    [course?.viewers],
  );

  const theemaLicense = data?.threemaLicenseCount[0];
  const theemaLicenseCount = theemaLicense?.licenseCount ?? 0;
  const usedThreemaLicenses = data?.usedThreemaLicenses?.length ?? 0;
  const availableThreemaLicenses = theemaLicenseCount - usedThreemaLicenses;

  // Bulk actions for teachers
  const {
    selectedPersonUuids: selectedTeacherUuids,
    handlePersonSelect: handleTeacherSelect,
    handleSelectAll: handleSelectAllTeachers,
    clearSelection: clearTeacherSelection,
    handleAddThreema: handleAddThreemaTeachers,
    handleBulkNextcloud: handleBulkNextcloudTeachers,
    ConfirmationDialogs: TeacherConfirmationDialogs,
  } = usePersonBulkActions({
    freeThreemaLicenseCount: availableThreemaLicenses,
  });

  // Bulk actions for students
  const {
    selectedPersonUuids: selectedStudentUuids,
    handlePersonSelect: handleStudentSelect,
    handleSelectAll: handleSelectAllStudents,
    clearSelection: clearStudentSelection,
    handleAddThreema: handleAddThreemaStudents,
    handleBulkNextcloud: handleBulkNextcloudStudents,
    ConfirmationDialogs: StudentConfirmationDialogs,
  } = usePersonBulkActions({
    freeThreemaLicenseCount: availableThreemaLicenses,
  });

  const emptySelection =
    (threemaLicense || cloudLicense) && selectedTeacherUuids.length + selectedStudentUuids.length === 0;

  const canEditCloudPermissions =
    permissions.canManageThreema(pimAuthClaims.getOrganization()) ||
    permissions.canManageNextcloud(pimAuthClaims.getOrganization());

  const canEditCourseMembers = courseUuid
    ? permissions.canChangeBpCoursePermissions({
        uuid: courseUuid,
        organization: { uuid: myOrganizationUuid },
      })
    : false;

  const AddButton: ReactNode = (
    <Dropdown
      triggerClass={styles.button}
      disabled={!isBpCourse}
      trigger={
        <Button disabled={!isBpCourse} icon={<AddIcon />} hierarchy='tertiary' onClick={() => handleMembersEdit()}>
          {t('courses.addMembers')}
        </Button>
      }
    >
      <DropdownMenu
        data={[
          {
            label: t('rolesInOrganization.teacher', { count: 2 }),
            onClick: () => handleAddMember(GroupRoles.Editor),
          },
          {
            label: t('rolesInOrganization.student', { count: 2 }),
            onClick: () => handleAddMember(GroupRoles.Viewer),
          },
        ]}
      />
    </Dropdown>
  );

  function getActions(personUuid: string): ReactNode {
    const allPersons = [...teachers, ...students];
    const person = allPersons.find((p) => p.uuid === personUuid);
    if (!person) return [];

    const items: DropdownMenuItem[] = [];

    const { uuid, organizationUuid, groupRole, profileRole, userStatus, firstName, lastName } = person;

    const canMessage = permissions.canDirectMessageWithinCourse(
      {
        uuid: courseUuid ?? '',
        organization: { uuid: organizationUuid },
      },
      {
        uuid: uuid,
        organization: { uuid: organizationUuid },
        role: profileRole,
      },
    );

    if (canEditCourseMembers) {
      items.push({
        label: t('courses.roleInCourse'),
        subContent: [
          {
            label: t('rolesInOrganization.teacher', { count: 1 }),
            onClick: () => {
              handleGroupRoleChange(uuid, 'teacher');
            },
            disabled: groupRole === GroupRoles.Editor,
          },
          {
            label: t('rolesInOrganization.student', { count: 1 }),
            onClick: () => {
              handleGroupRoleChange(uuid, 'student');
            },
            disabled: groupRole === GroupRoles.Viewer,
          },
        ],
      });

      if (userStatus === UserStatus.None) {
        items.push({
          label: t('profiles.invite.sendInvitation'),
          disabled: userStatus !== UserStatus.None,
          onClick: () => {
            handleInvite(uuid, userStatus);
          },
        });
      } else if (userStatus === UserStatus.Invited) {
        items.push({
          label: t('profiles.invite.resendInvitation'),
          disabled: userStatus !== UserStatus.Invited,
          onClick: () => {
            handleInvite(uuid, userStatus);
          },
        });
      }

      items.push({ type: 'ruler' });

      items.push({
        label: t('courses.removeMember'),
        color: 'error',
        onClick: () => {
          handleRemoveMember(uuid, `${firstName} ${lastName}`);
        },
        disabled: !isBpCourse,
      });
    }

    return (
      <>
        <Button
          hierarchy='ghost'
          icon={<SendIcon />}
          onClick={() => handleChat(uuid, profileRole)}
          disabled={!accountHasPerson(userStatus) || !canMessage}
        />
        {organizationUuid === myOrganizationUuid && items.length > 0 && (
          <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
            <DropdownMenu data={items} />
          </Dropdown>
        )}
      </>
    );
  }

  async function handleGroupRoleChange(uuid: string, target: 'teacher' | 'student') {
    if (!courseUuid) return;

    setLoadingModalOpen(true);
    setLoadingMessage(t('courses.changeGroupRoleInProgess'));

    let teacherUuids: string[] = teachers.map((t) => t.uuid) ?? [];
    let studentUuids: string[] = students.map((s) => s.uuid) ?? [];

    if (target === 'teacher') {
      teacherUuids.push(uuid);
      studentUuids = studentUuids.filter((s) => s !== uuid);
    } else {
      teacherUuids = teacherUuids.filter((t) => t !== uuid);
      studentUuids.push(uuid);
    }

    const { error } = await bpUpdateGroup({
      uuid: courseUuid,
      update: { editors: teacherUuids, viewers: studentUuids },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    reset();
  }

  function handleInvite(uuid: string, userStatus: string) {
    setPersonData({ uuid: uuid, status: userStatus });
    setInviteModalOpen(true);
  }

  async function handleChat(uuid: string, role: ProfileRoles) {
    if (!courseUuid) return;

    setLoadingModalOpen(true);

    await redirectToDmMessagePage({
      groupUuid: courseUuid,
      targetProfileUuid: uuid,
      targetProfileOrganizationUuid: '',
      targetProfileRole: role,
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      context: 'course',
      permissionChecker: permissions,
      navigate,
    });

    reset();
  }

  function handleAddMember(role: GroupRoles) {
    setPersonData({ addContext: role });
    setMembersModalOpen(true);
  }

  async function handleRemoveMember(uuid: string, name: string) {
    if (!courseUuid) return;

    const confirm = await confirmRemove({
      message: t('courses.removeMemberMessage', { name }),
    });
    if (!confirm) return;

    setLoadingModalOpen(true);
    setLoadingMessage(t('courses.removeMemberInProgress'));

    const teacherUuids = teachers.filter((t) => t.uuid !== uuid).map((t) => t.uuid);
    const studentUuids = students.filter((s) => s.uuid !== uuid).map((s) => s.uuid);

    const { error } = await bpUpdateGroup({
      uuid: courseUuid,
      update: { editors: teacherUuids, viewers: studentUuids },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    reset();
  }

  function handleLabelAdd(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      }
      return prev;
    });
  }

  function handleLabelClose(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (labelIsInLabels(label, prev)) {
        return prev.filter((l) => l !== label);
      }
      return prev;
    });
  }

  function handleCloseAllLabels() {
    setSelectedLabels([]);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function handleSourceFilter(sources: PersonLabelSource[]) {
    setActiveSources(sources);
  }

  function handleFilterModeChange(source: PersonLabelSource, mode: 'and' | 'or') {
    setFilterMode((prev) => ({
      ...prev,
      [source]: mode,
    }));
  }
  function handleMembersEdit() {
    setMembersModalOpen(true);
  }

  function reset() {
    setPersonData(null);
    setInviteModalOpen(false);
    setMembersModalOpen(false);
    setLoadingModalOpen(false);
    setLoadingMessage(null);
    clearTeacherSelection();
    clearStudentSelection();
    refetchCourseData({ requestPolicy: 'network-only' });
  }

  async function handleBulkNextcloud() {
    await Promise.all([
      selectedTeacherUuids.length > 0 ? handleBulkNextcloudTeachers(selectedTeacherUuids) : Promise.resolve(),
      selectedStudentUuids.length > 0 ? handleBulkNextcloudStudents(selectedStudentUuids) : Promise.resolve(),
    ]);
  }

  async function handleAddThreema() {
    await Promise.all([
      selectedTeacherUuids.length > 0 ? handleAddThreemaTeachers(selectedTeacherUuids) : Promise.resolve(),
      selectedStudentUuids.length > 0 ? handleAddThreemaStudents(selectedStudentUuids) : Promise.resolve(),
    ]);
  }

  const bulkEditContent: DropdownMenuItem[] = [
    ...(cloudLicense && canEditCloudPermissions
      ? [
          {
            label: t('externalServices.createCloudAccount'),
            onClick: () => handleBulkNextcloud(),
            disabled: emptySelection,
          },
        ]
      : []),
    ...(threemaLicense && canEditCloudPermissions
      ? [
          {
            label: t('externalServices.assignThreemaLicense'),
            onClick: () => handleAddThreema(),
            disabled: emptySelection,
          },
        ]
      : []),
  ];

  return (
    <BpSubpage className={styles['course-persons-subpage']}>
      <Grid>
        <GridRow spacingBottom='l'>
          <PersonsLabelFilter
            selectedLabels={selectedLabels}
            showSourcesFilter
            availableSources={['AddOn', 'Class', 'ClassGroup', 'ProfileRole', 'UserStatus']}
            activeSources={activeSources}
            filterMode={filterMode}
            showSearch
            showAddButton={canEditCourseMembers}
            addButtonContent={AddButton}
            showBulkEdit
            bulkEditDisabled={emptySelection}
            bulkEditContent={bulkEditContent}
            onLabelClose={(label) => handleLabelClose(label)}
            onCloseAll={() => handleCloseAllLabels()}
            onSearch={(value) => handleSearch(value)}
            onSourceFilter={(sources) => handleSourceFilter(sources)}
            onFilterModeChange={handleFilterModeChange}
            data={data}
          />
        </GridRow>

        {organizations?.map((organization) => {
          const uuid = organization.uuid;

          const organizationAdmins = admins.filter((a) => a.organization.uuid === uuid);
          const organizationTeachers = teachers.filter((e) => e.organizationUuid === uuid);
          const organizationStudents = students.filter((v) => v.organizationUuid === uuid);

          return (
            <div key={uuid} className={styles.organization}>
              <BpHeader
                size={organizations.length > 1 ? 'm' : 's'}
                headline={
                  organizations.length > 1
                    ? organization.uuid === myOrganizationUuid
                      ? t('institution.myInstitution')
                      : (organization?.name ?? '')
                    : t('institution.admin')
                }
                subHeadline={
                  organizations.length > 1
                    ? `${t('institution.admin')}: ${organizationAdmins.map((a) => a.displayName).join(', ')}`
                    : organizationAdmins.map((a) => a.displayName).join(', ')
                }
                noMargin
              />
              <GridRow mobileGap='var(--spacing-5)'>
                <GridColumn width={6}>
                  <BpCard
                    header={{
                      headline: t('rolesInOrganization.teacher', { count: organizationTeachers.length }),
                      subHeadline: organizationTeachers.length,
                    }}
                    noPadding
                  >
                    <SimplePersonsList
                      persons={organizationTeachers}
                      selectedLabels={selectedLabels}
                      search={search}
                      activeSources={activeSources}
                      onGetActions={(uuid) => getActions(uuid)}
                      padding='m'
                      onLabelClick={(label) => handleLabelAdd(label)}
                      data={data}
                      onSelectCheckbox={(uuid) => handleTeacherSelect(uuid)}
                      selectedPersons={selectedTeacherUuids}
                      onSelectAll={handleSelectAllTeachers}
                    />
                  </BpCard>
                </GridColumn>
                <GridColumn width={6}>
                  <BpCard
                    header={{
                      headline: t('rolesInOrganization.student', { count: organizationStudents.length }),
                      subHeadline: organizationStudents.length,
                    }}
                    noPadding
                  >
                    <SimplePersonsList
                      persons={organizationStudents}
                      selectedLabels={selectedLabels}
                      search={search}
                      activeSources={activeSources}
                      onGetActions={(uuid) => getActions(uuid)}
                      padding='m'
                      onLabelClick={(label) => handleLabelAdd(label)}
                      data={data}
                      onSelectCheckbox={(uuid) => handleStudentSelect(uuid)}
                      selectedPersons={selectedStudentUuids}
                      onSelectAll={handleSelectAllStudents}
                    />
                  </BpCard>
                </GridColumn>
              </GridRow>
            </div>
          );
        })}
      </Grid>

      {courseUuid && personData?.addContext && (
        <CourseMembersModal
          courseUuid={courseUuid}
          role={personData.addContext}
          isOpen={membersModalOpen}
          onClose={() => reset()}
        />
      )}

      {personData && personData.uuid && personData.status && (
        <CreateUserModal
          isOpen={inviteModalOpen}
          profileUuid={personData.uuid}
          resend={personData.status === UserStatus.Invited}
          onClose={() => reset()}
        />
      )}

      <LoadingModal
        isOpen={loadingModalOpen}
        message={loadingMessage ?? undefined}
        onClose={() => {
          setLoadingModalOpen(false);
          setLoadingMessage(null);
        }}
      />

      <ConfirmationDialog />
      <TeacherConfirmationDialogs />
      <StudentConfirmationDialogs />
    </BpSubpage>
  );
};
